import { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Alert from "../Components/Utils/Alert";

const AlertContext = createContext(null);

/** Başarı için varsayılan süre (ms) */
export const DURATION_SUCCESS = 5500;
/** Hata için varsayılan süre (ms) — okunabilirlik için daha uzun */
export const DURATION_ERROR = 7000;
/** Uyarı için varsayılan süre (ms) */
export const DURATION_WARNING = 5500;

export const AlertProvider = ({ children }) => {
  const [state, setState] = useState({
    visible: false,
    variant: "success",
    message: "",
    duration: DURATION_SUCCESS,
  });
  const timerRef = useRef(null);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setState((prev) => ({ ...prev, visible: false }));
  }, []);

  const show = useCallback((variant, message, duration) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const d = duration ?? (variant === "error" ? DURATION_ERROR : DURATION_SUCCESS);
    setState({ visible: true, variant, message: message || "", duration: d });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setState((prev) => ({ ...prev, visible: false }));
    }, d);
  }, []);

  const showSuccess = useCallback((message) => show("success", message, DURATION_SUCCESS), [show]);
  const showError = useCallback((message) => show("error", message, DURATION_ERROR), [show]);
  const showWarning = useCallback((message) => show("warning", message, DURATION_WARNING), [show]);

  return (
    <AlertContext.Provider
      value={{
        ...state,
        hide,
        showSuccess,
        showError,
        showWarning,
      }}
    >
      {children}
      {state.visible &&
        createPortal(
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[99999] pointer-events-none">
            <Alert
              variant={state.variant}
              message={state.message}
              onClose={hide}
              duration={state.duration}
            />
          </div>,
          document.body,
        )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    return {
      showSuccess: () => {},
      showError: () => {},
      showWarning: () => {},
      hide: () => {},
    };
  }
  return ctx;
};
